import { ApplicationConfig, LOCALE_ID, isDevMode, APP_INITIALIZER, provideZoneChangeDetection, ErrorHandler, inject, provideAppInitializer } from '@angular/core';
import { AuthService } from './core/services/auth.service';
import { JourneyErrorHandler, JourneyInterceptor } from './core/services/journey.service';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS, withXhr } from '@angular/common/http';
import { routes } from './app.routes';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { SessionTimeoutInterceptor } from './core/interceptors/session-timeout.interceptor';
import { LoadingInterceptor } from './core/interceptors/loading.interceptor';
import { TimeoutInterceptor } from './core/interceptors/timeout.interceptor';
import { RetryInterceptor } from './core/interceptors/retry.interceptor';
import { ErrorInterceptor } from './core/interceptors/error.interceptor';
import { DedupeRequestInterceptor } from './core/interceptors/dedupe-request.interceptor';
import { provideServiceWorker } from '@angular/service-worker';
import { SelectEnhancerService } from './shared/services/select-enhancer.service';
import { APP_LOCALE, registerIndianNumbers } from './core/utils/indian-numbers';

registerIndianNumbers();

export const appConfig: ApplicationConfig = {
  providers: [
    // Amounts read ₹1,25,000 on every screen, as they do on receipts. Dates are not affected: see indian-numbers.ts.
    { provide: LOCALE_ID, useValue: APP_LOCALE },
    // Zone-based change detection, as the app was written for (ANGULAR_UPGRADE_PLAN: "we stay zone-based").
    // Angular v21+ switched to ZONELESS by default when this provider is absent — after the upgrade, plain
    // fields set in HTTP/timer callbacks stopped refreshing the screen until the next click (stuck spinners,
    // e.g. Insurance Claims). zone.js is already loaded in polyfills; this re-enables it.
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    // Restores this tab's sign-in BEFORE the first navigation and outside any constructor (DISC-007). When the
    // stored token is expired it waits for the start-up renewal (≤20 s, boot splash showing) so no screen, poll or
    // SignalR start sees an expired token. restoreOnStartup never rejects.
    provideAppInitializer(() => inject(AuthService).restoreOnStartup()),

    // ── HTTP INTERCEPTOR CHAIN ──────────────────────────────────────
    // Request flow (top → bottom) | Response flow (bottom → top)
    //
    // 0. DedupeRequestInterceptor — drops duplicate POST/PUT/PATCH within 1.2s
    //                              (FIRST so it short-circuits before auth/network)
    // 1. AuthInterceptor         — attaches JWT, handles 401 logout
    // 2. SessionTimeoutInterceptor — tracks idle session
    // 3. LoadingInterceptor      — global spinner tracking
    // 4. TimeoutInterceptor      — 30s default, configurable via X-Timeout
    // 5. RetryInterceptor        — exponential backoff for GET/5xx/network
    // 6. ErrorInterceptor        — classifies errors, shows toasts (LAST)
    // (-1. JourneyInterceptor  — tags requests with X-Request-Id / X-Client-Session for the owner's user-journey
    //                             view and notes requests that never got an answer. OUTERMOST, so it sees the final
    //                             outcome after retries.)
    { provide: HTTP_INTERCEPTORS, useClass: JourneyInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: DedupeRequestInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: SessionTimeoutInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TimeoutInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: RetryInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },

    // Uncaught errors → owner's user-journey view (then the usual console report).
    { provide: ErrorHandler, useClass: JourneyErrorHandler },

    // Caches the app's own files only. ngsw-config.json has NO dataGroups (DISC-012), and must never get one: AuthService
    // deletes every ':data:' store at each page load and sign-out, so a data group would silently never work. An API group
    // would also be unsafe: replies are per person but the worker keys them by address only, so on a shared PC it would
    // show one person's data to the next. AuthInterceptor sends 'ngsw-bypass' on every API call, which needs the API CORS
    // policy to allow that header. PwaUpdateComponent asks for new versions itself, because bypassed API calls no longer
    // start the worker's update check.
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000'
    }),

    // ── Native <select> auto-enhancement ───────────────────────────
    // Replaces every native <select> in the app with a styled custom
    // dropdown overlay (the panel always fits inside the page width, no
    // browser-native rendering overflow). See SelectEnhancerService.
    // Opt-out per element with the `data-no-replace` attribute.
    {
      provide: APP_INITIALIZER,
      useFactory: (svc: SelectEnhancerService) => () => svc.init(),
      deps: [SelectEnhancerService],
      multi: true
    }
  ]
};
